import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Body7, ConfirmModal, Container, NumberField, Select, TextField } from '@ui';
import { FlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { showSuccessBanner } from '@utils/application';
import { useGptModels } from '@hooks/features/useGptModels';
import { SegmentedControl } from '@features/scoreSpec/SegmentedControl';
import { TENANT_NAME_MAX, TENANT_SETTING_KEYS, updateTenant } from '@services/fmdc';
import { isDefaultSetting, settingsDiff, settingsDraftFrom } from './settingsDiff';
import { Card, SectionHead } from './styles';

// PATCH /tenant: the client's name, whether data capture is on, and the extraction settings. One
// Save sends only what changed — `settingsDiff` for the settings (a cleared key goes as null, which
// the API reads as "reset to default"), plus name / enabled when they moved.
//
// Every setting the server holds no value for shows a "default" marker; the admin never has to
// guess which numbers are theirs and which are the engine's.

const ENABLED_SEGMENTS = [
  { key: 'on', label: 'On' },
  { key: 'off', label: 'Off' },
];

// A tri-state select for the boolean settings: a checkbox cannot say "not set", and "not set"
// is the state that means the engine's default applies.
const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'On' },
  { value: 'false', label: 'Off' },
];

const OTHER_MODEL = '__other__';

const FieldGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const DefaultMarker = () => <Body7 isLowContrast>default</Body7>;

const FieldLabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  min-height: 14px;
`;

// The model as a Select over the app's catalog (the ModelSelect pattern), with an "Other…" that
// reveals free text, and free text alone when the catalog is empty.
const ModelField = ({ value, onChange }) => {
  const { models } = useGptModels();
  const current = value || '';
  const [wantsCustom, setWantsCustom] = React.useState(false);
  const isListed = models.some((model) => model.id === current);

  if (models.length === 0) {
    return (
      <TextField
        label="Model"
        mono
        value={current}
        onChange={(next) => onChange(next || null)}
        placeholder="gpt-5.2"
        hint="The model catalog is unavailable, so this is free text. Empty means the default."
      />
    );
  }

  const options = models
    .map((model) => ({
      value: model.id,
      label: model.display_name && model.display_name !== model.id ? `${model.display_name} — ${model.id}` : model.id,
    }))
    .concat(current && !isListed ? [{ value: current, label: `${current} — not in the catalog` }] : [])
    .concat([{ value: OTHER_MODEL, label: 'Other…' }]);

  return (
    <div>
      <Select
        label="Model"
        value={wantsCustom ? OTHER_MODEL : current}
        placeholder="Default"
        options={options}
        onChange={(next) => {
          if (next === OTHER_MODEL) {
            setWantsCustom(true);
            return;
          }
          setWantsCustom(false);
          onChange(next || null);
        }}
      />
      {wantsCustom && (
        <Container top={8}>
          <TextField
            aria-label="Model — custom"
            mono
            value={current}
            onChange={(next) => onChange(next || null)}
            placeholder="gpt-5.2"
            hint="Not checked here. The extraction engine rejects a model it does not know."
          />
        </Container>
      )}
    </div>
  );
};

const booleanValue = (value) => (value === null || value === undefined ? '' : String(value));
const booleanFrom = (text) => (text === '' ? null : text === 'true');

export const SettingsForm = ({ tenant, onSaved }) => {
  const dispatch = useDispatch();
  const serverSettings = tenant?.settings || {};

  const [name, setName] = React.useState(tenant?.name || '');
  const [enabled, setEnabled] = React.useState(tenant?.enabled !== false);
  const [draft, setDraft] = React.useState(() => settingsDraftFrom(serverSettings));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [confirmingOff, setConfirmingOff] = React.useState(false);

  // A reload after a save hands down a fresh tenant; reset the draft to it so "changed" is measured
  // against what the server now holds. Keyed on the VALUES, not the object: the provider replaces
  // the tenant object on every 5 s poll while a setup runs or embeddings refresh, and a reset on
  // identity wiped what the admin was typing on each tick.
  const serverSignature = JSON.stringify([
    tenant?.name || '',
    tenant?.enabled !== false,
    settingsDraftFrom(tenant?.settings || {}),
  ]);
  React.useEffect(() => {
    setName(tenant?.name || '');
    setEnabled(tenant?.enabled !== false);
    setDraft(settingsDraftFrom(tenant?.settings || {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverSignature]);

  const setKey = (key) => (value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const settingsPatch = settingsDiff(serverSettings, draft);
  const nameChanged = name.trim() !== (tenant?.name || '');
  const enabledChanged = enabled !== (tenant?.enabled !== false);
  const nameInvalid = name.trim().length > TENANT_NAME_MAX;
  const hasChanges = nameChanged || enabledChanged || Object.keys(settingsPatch).length > 0;
  const canSave = hasChanges && !busy && !nameInvalid;

  const handleSave = async () => {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    const body = {};
    if (nameChanged) body.name = name.trim();
    if (enabledChanged) body.enabled = enabled;
    if (Object.keys(settingsPatch).length > 0) body.settings = settingsPatch;
    const result = await updateTenant(body);
    setBusy(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    showSuccessBanner('Settings saved', null, dispatch);
    if (onSaved) onSaved(result.tenant);
  };

  const handleEnabledSelect = (key) => {
    if (key === 'off' && enabled) {
      setConfirmingOff(true);
      return;
    }
    setEnabled(key === 'on');
  };

  const saveHint = busy
    ? null
    : nameInvalid
    ? `Name must be ${TENANT_NAME_MAX} characters or fewer.`
    : !hasChanges
    ? 'Nothing has changed.'
    : null;

  return (
    <Card>
      <SectionHead
        heading="Settings"
        subtext="Only what you change is sent. Clearing a value resets it to the default."
      />

      <Container top={16}>
        <FieldGrid>
          <TextField
            label="Client name"
            value={name}
            onChange={setName}
            invalid={nameInvalid}
            hint={nameInvalid ? `Up to ${TENANT_NAME_MAX} characters.` : undefined}
          />
          <div>
            {/* Labelled the way the TextField beside it is (Body7, sentence case), so the row reads
                as one hierarchy; the control keeps "Data capture" as its accessible name. */}
            <Body7>Data capture</Body7>
            <Container top={4}>
              <SegmentedControl
                label="Data capture"
                options={ENABLED_SEGMENTS}
                active={enabled ? 'on' : 'off'}
                onSelect={handleEnabledSelect}
                segmentWidth={72}
                hideLabel
              />
            </Container>
            {!enabled && (
              <Container top={6}>
                <Body7 isLowContrast>While off, every page except Settings answers 404 for members.</Body7>
              </Container>
            )}
          </div>
        </FieldGrid>
      </Container>

      <Container top={24}>
        <SectionHead
          heading="Extraction"
          subtext="Model and limits for the classifier. Leave a field empty to use the engine’s default."
        />
      </Container>

      <Container top={12}>
        <FieldGrid>
          {TENANT_SETTING_KEYS.map(({ key, kind, label }) => {
            const isDefault = isDefaultSetting(draft, key);
            if (kind === 'model') {
              return (
                <div key={key}>
                  <FieldLabelRow>
                    <span />
                    {isDefault && <DefaultMarker />}
                  </FieldLabelRow>
                  <ModelField value={draft[key]} onChange={setKey(key)} />
                </div>
              );
            }
            if (kind === 'boolean') {
              return (
                <div key={key}>
                  <FieldLabelRow>
                    <span />
                    {isDefault && <DefaultMarker />}
                  </FieldLabelRow>
                  <Select
                    label={label}
                    value={booleanValue(draft[key])}
                    placeholder="Default"
                    options={BOOLEAN_OPTIONS}
                    onChange={(next) => setKey(key)(booleanFrom(next))}
                  />
                </div>
              );
            }
            return (
              <div key={key}>
                <FieldLabelRow>
                  <span />
                  {isDefault && <DefaultMarker />}
                </FieldLabelRow>
                <NumberField label={label} value={draft[key]} onChange={setKey(key)} min={0} step={1} />
              </div>
            );
          })}
        </FieldGrid>
      </Container>

      {error && (
        <Container top={16}>
          <ErrorText>{error}</ErrorText>
        </Container>
      )}

      <Container top={24} row verticallyCenter flexWrap gap={12}>
        <FlatButton onClick={handleSave} disabled={!canSave} buttonProps={{ 'aria-disabled': !canSave }}>
          {busy ? 'Saving…' : 'Save'}
        </FlatButton>
        {saveHint && <Body7 isLowContrast>{saveHint}</Body7>}
      </Container>

      {confirmingOff && (
        <ConfirmModal
          title="Switch data capture off?"
          body="While it is off, every page except Settings answers 404 for members, and uploads are refused. Nothing is deleted; switch it back on here at any time."
          confirmLabel="Switch off"
          onConfirm={() => {
            setEnabled(false);
            setConfirmingOff(false);
          }}
          onCancel={() => setConfirmingOff(false)}
        />
      )}
    </Card>
  );
};
